import { MorphemeTilePresentationProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import filledArray from "@-/com.npmjs.filled-array";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  type MorphemeGlyphTileStage,
  useMorphemeListStagesQuery,
} from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { MorphemeDetailsTileLink } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.details";
import chunk from "@-/com.npmjs.just-split";
import { useCallback, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useBrowseMorphemePageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.morpheme.navigator";
import { Platform, View } from "@-/com.npmjs.react-native";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import {
  HeaderLegendList,
  type HeaderLegendListScrollPosition,
} from "@-/dev.improve.haniverse.components.ui.header-scroll";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";

// Horizontal padding on the outside of the grid matches the inter-card gap so
// the grid reads as evenly spaced columns to the edges of the container.
const GRID_GAP = 8;
const GRID_HORIZONTAL_PADDING = GRID_GAP;
const DEFAULT_COLUMN_COUNT = 4;
/** A surface body row's 1px side border plus its 4px (`px-1`) inset, per side. */
const SURFACE_BODY_INSET = 5;
const SURFACE_BODY_CLASS_NAME = "border-x border-border/60 bg-surface px-1";

function computeTileSize(
  availableWidth: number,
  columnCount: number,
  compact: boolean,
  horizontalPadding: number,
) {
  const gapTotal = (compact ? 4 : GRID_GAP) * (columnCount - 1);
  const size = (availableWidth - horizontalPadding * 2 - gapTotal) / columnCount;
  return Math.max(1, compact ? size : Math.floor(size));
}

type ListRow = { cards: MorphemeDetailsTileSlice[]; kind: "cards" } | { kind: "divider" };

/**
 * Splits a list into two sections: the cards from `index` on are drawn below `element`, a row
 * `height` points tall (e.g. Search's "Partial matches" divider).
 */
export type MorphemeDetailsListDivider = {
  element: ReactElement;
  height: number;
  index: number;
};

// `@legendapp/list`'s web build spreads any unrecognized prop straight onto the
// underlying scroll `div`, so the native-only `keyboardShouldPersistTaps` /
// `keyboardDismissMode` ScrollView props leak as unknown DOM attributes and
// trigger React's "does not recognize the prop" warning. They're meaningless on
// web anyway, so omit them there and keep them only on native.
const keyboardScrollProps = (keyboardDismissMode: "none" | "on-drag" | "interactive") =>
  Platform.OS === "web"
    ? {}
    : ({ keyboardDismissMode, keyboardShouldPersistTaps: "handled" } as const);

function getRowKey(row: ListRow): string {
  return row.kind === "divider"
    ? "divider"
    : row.cards.map((card) => card.id ?? card.front).join(",");
}

/**
 * A static cell's semantic key. A row may repeat a card (a pronunciation level holds one slot per
 * reading, e.g. 干 twice on 1-P1), so a repeat is suffixed with its occurrence in the row.
 */
function staticCellKey(cards: readonly MorphemeDetailsTileSlice[], column: number): string {
  const card = cards[column];
  const key = String(card?.id ?? card?.front);
  const occurrence = cards
    .slice(0, column)
    .filter((other) => String(other.id ?? other.front) === key).length;
  return occurrence === 0 ? key : `${key}:${occurrence}`;
}

function MorphemeDetailsTileListContent({
  bottomInset = 0,
  cards,
  columnCount = DEFAULT_COLUMN_COUNT,
  compact = false,
  divider,
  emptyCellContent,
  getStage,
  headerOverlap,
  horizontalPadding = compact ? 9 : GRID_HORIZONTAL_PADDING,
  isEditing = false,
  keyboardDismissMode = "on-drag",
  ListFooterComponent,
  ListHeaderComponent,
  onContentSizeChange,
  onEndReached,
  onGraphemePress,
  onNearEndChange,
  onRemoveCard,
  renderCardOverlay,
  scrollable = true,
  scrollPosition,
  surface = false,
  topPadding,
  verticalPadding = compact ? 9 : GRID_HORIZONTAL_PADDING,
}: {
  bottomInset?: number;
  cards?: MorphemeDetailsTileSlice[];
  columnCount?: number;
  /** Match the decks grid's 4px gaps and 9px outer padding. */
  compact?: boolean;
  /** Starts a second section of the grid on a row of its own, below a divider. */
  divider?: MorphemeDetailsListDivider;
  /**
   * Fills each empty cell that pads a short last row out to `columnCount`, e.g. a loading
   * skeleton while more cards are on the way. Sized to the cell; empty cells stay blank without it.
   */
  emptyCellContent?: ReactElement | null;
  /**
   * A card's stage, when its tile stands for one of the grapheme's study cards rather than the
   * grapheme as a whole (a level's, see `useLevelTileData`). Defaults to the batched stage read.
   */
  getStage?: (front: string) => MorphemeGlyphTileStage;
  /** See `HeaderLegendList`'s `headerOverlap`: chrome floating over the top of the viewport. */
  headerOverlap?: number;
  /**
   * Horizontal inset inside the grid, including when embedded in a surface. With `surface`, the
   * inset outside the surface's side borders.
   */
  horizontalPadding?: number;
  isEditing?: boolean;
  keyboardDismissMode?: "none" | "on-drag" | "interactive";
  ListFooterComponent?: ReactElement | null;
  /** Scrolls above the first row; with `surface`, the top of that surface. */
  ListHeaderComponent?: ReactElement | null;
  onContentSizeChange?: (width: number, height: number) => void;
  onEndReached?: () => void;
  onGraphemePress?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  /** See `HeaderLegendList`'s `onNearEndChange`; scrollable lists only. */
  onNearEndChange?: (isNearEnd: boolean) => void;
  onRemoveCard?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  /** Laid over a card's cell, keyed by the card's front, e.g. a Browse level's slide-lesson dots. */
  renderCardOverlay?: (front: string) => ReactNode;
  scrollable?: boolean;
  /** Reopen at the row the learner left when the list is released and rebuilt. */
  scrollPosition?: HeaderLegendListScrollPosition;
  /**
   * Draw the rows as the body of a `Surface` whose top is `ListHeaderComponent` (a `Surface`
   * with `rounded-b-none border-b-0`, as on the Stats stage surface). Each row carries the
   * surface's side borders, background and 4px inset, and the footer closes its bottom edge, so a
   * virtualized grid reads as one bordered card. Compact grids only.
   */
  surface?: boolean;
  /**
   * Rest the first row exactly this far below the top of the scroll viewport, instead of the
   * grid padding floored to the shared header gap. For screens that measure the gap themselves.
   */
  topPadding?: number;
  /** Padding above and below the grid; embedded surface bodies can supply their own inset. */
  verticalPadding?: number;
}) {
  const isFocused = useIsNavigationFocused();
  const { push } = useBrowseMorphemePageNavigator();
  const pushMorphemePage = push.mutateAsync;
  const pendingFrontsRef = useRef(new Set<string>());
  const [pendingFronts, setPendingFronts] = useState<ReadonlySet<string>>(() => new Set());
  const onNavigate = useCallback(
    (card: MorphemeDetailsTileSlice) => {
      const front = card.front;
      if (pendingFrontsRef.current.has(front)) {
        return;
      }
      pendingFrontsRef.current.add(front);
      setPendingFronts(new Set(pendingFrontsRef.current));
      // A MutationObserver follows only its latest mutation. Keep every in-flight front's shade
      // until its own push settles, even if a second tile is tapped or a row is recycled.
      // The navigator still owns error reporting.
      void pushMorphemePage({ morpheme: front, params: { view: "mnemonics" } })
        .catch(() => undefined)
        .finally(() => {
          pendingFrontsRef.current.delete(front);
          setPendingFronts(new Set(pendingFrontsRef.current));
        });
    },
    [pushMorphemePage],
  );
  const [windowWidth] = useContextSelectors(RootContext, (c) => c.windowDimensions.width);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const widthForLayout = containerWidth ?? windowWidth;
  const tileSize = computeTileSize(
    widthForLayout,
    columnCount,
    compact,
    horizontalPadding + (surface ? SURFACE_BODY_INSET : 0),
  );
  const rowHeight = tileSize + (compact ? 4 : GRID_GAP);
  const dividerIndex = divider?.index;
  const rows = useMemo<ListRow[]>(() => {
    const toRows = (list: MorphemeDetailsTileSlice[]) =>
      chunk(list, columnCount).map((row): ListRow => ({ cards: row, kind: "cards" }));
    if (dividerIndex === undefined) {
      return toRows(cards ?? []);
    }
    return [
      ...toRows((cards ?? []).slice(0, dividerIndex)),
      { kind: "divider" },
      ...toRows((cards ?? []).slice(dividerIndex)),
    ];
  }, [cards, columnCount, dividerIndex]);

  // Resolve every tile's SRS stage in ONE batched query here instead of one
  // per tile. Each `MorphemeDetailsTileUi` used to fire its own stage
  // `useQuery` + change subscription; a multi-row reveal write then restaged
  // every mounted grapheme's key individually — the per-tile query storm this
  // lift removes (see `useMorphemeListStagesQuery`).
  const allFronts = useMemo(() => uniq((cards ?? []).map((card) => card.front)), [cards]);
  const { data: stageByGrapheme } = useQuery({
    ...useMorphemeListStagesQuery(allFronts),
    placeholderData: keepPreviousData,
  });
  const rowState = useMemo(
    () => ({
      tileSize,
      divider,
      emptyCellContent,
      getStage,
      isEditing,
      isFocused,
      onGraphemePress,
      onRemoveCard,
      pendingFronts,
      renderCardOverlay,
      rows,
      stageByGrapheme,
    }),
    [
      tileSize,
      divider,
      emptyCellContent,
      getStage,
      isEditing,
      isFocused,
      onGraphemePress,
      onRemoveCard,
      pendingFronts,
      renderCardOverlay,
      rows,
      stageByGrapheme,
    ],
  );

  const renderRow = (item: ListRow, rowKey: string | undefined, isLastRow: boolean) =>
    item.kind === "divider" ? (
      <View key={rowKey}>{divider?.element}</View>
    ) : (
      <View
        className={`${
          compact ? (isLastRow ? "flex-row gap-1" : "flex-row gap-1 pb-1") : "flex-row gap-2 pb-2"
        } ${surface ? SURFACE_BODY_CLASS_NAME : ""}`}
        key={rowKey}
      >
        {item.cards.map((card, column) => (
          <View
            className="relative overflow-hidden"
            // A recycled row keeps its native cells. Selection and pending navigation
            // are keyed by the current front, not by a cell instance. Static lists
            // still use semantic keys because they are not recycling containers.
            key={scrollable ? column : staticCellKey(item.cards, column)}
            style={{ height: tileSize, width: tileSize }}
            testID={`morpheme-details-tile-cell-${card.front}`}
          >
            {/* Keep the native list and exact cell geometry; inactive tabs need none of the
              tile's glyph, selection, navigator or typography view trees. */}
            {isFocused ? (
              <>
                <MorphemeDetailsTileLink
                  graphemeCard={card}
                  isEditing={isEditing}
                  isNavigating={pendingFronts.has(card.front)}
                  onGraphemePress={onGraphemePress}
                  onNavigate={onNavigate}
                  onRemoveCard={onRemoveCard}
                  size={tileSize}
                  stage={getStage?.(card.front) ?? stageByGrapheme?.[card.front] ?? "unadded"}
                />
                {renderCardOverlay?.(card.front)}
              </>
            ) : null}
          </View>
        ))}
        {filledArray(
          (index) => (
            <View
              className="relative overflow-hidden"
              key={`morpheme-details-tile-placeholder-${index}`}
              style={{ height: tileSize, width: tileSize }}
            >
              {/* Only the last row continues into more cards; one ending a section stays blank. */}
              {isLastRow ? emptyCellContent : null}
            </View>
          ),
          columnCount - arrayLength(item.cards),
        )}
      </View>
    );

  // The surface's body starts 4px below its header, as its rows sit 4px inside its side borders,
  // and closes the same 4px below the last row (or the footer, e.g. a loading row).
  const listHeader = surface ? (
    <>
      {ListHeaderComponent}
      <View className={`h-1 ${SURFACE_BODY_CLASS_NAME}`} />
    </>
  ) : (
    ListHeaderComponent
  );
  const listFooter = surface ? (
    <View className={`rounded-b-xs border-b pb-1 ${SURFACE_BODY_CLASS_NAME}`}>
      {ListFooterComponent}
    </View>
  ) : (
    ListFooterComponent
  );

  const handleLayout = (event: { nativeEvent: { layout: { width: number } } }) => {
    const measuredWidth = event.nativeEvent.layout.width;
    if (measuredWidth !== containerWidth && measuredWidth > 0) {
      setContainerWidth(measuredWidth);
    }
  };

  if (!scrollable) {
    return (
      <View
        onLayout={handleLayout}
        style={{
          // A surface's own edges space it; the caller places it with `topPadding`/`bottomInset`.
          paddingBottom: (surface ? 0 : verticalPadding) + bottomInset,
          paddingHorizontal: horizontalPadding,
          paddingTop: topPadding ?? (surface ? 0 : verticalPadding),
        }}
      >
        {listHeader}
        {rows.map((row, index) => renderRow(row, getRowKey(row), index === rows.length - 1))}
        {listFooter}
      </View>
    );
  }

  return (
    <View className="flex-1" onLayout={handleLayout}>
      <HeaderLegendList
        className="flex-1"
        contentContainerStyle={{
          paddingBottom: verticalPadding + bottomInset,
          paddingHorizontal: horizontalPadding,
          paddingTop: topPadding ?? verticalPadding,
        }}
        contentInsetAdjustmentBehavior="automatic"
        data={rows}
        // These square rows have exact bounds. Avoid allocating a pool from a guessed
        // row height, and keep only one row's scroll buffer around the visible grid.
        drawDistance={rowHeight}
        estimatedItemSize={rowHeight}
        // LegendList memoizes recycled row elements by item/data/extraData, not renderItem.
        // Publish focus explicitly so every retained row releases and restores its contents.
        extraData={rowState}
        getFixedItemSize={(item, index) => {
          if (item.kind === "divider") {
            return divider?.height ?? 0;
          }
          return compact && index === rows.length - 1 ? tileSize : rowHeight;
        }}
        headerOverlap={headerOverlap}
        {...keyboardScrollProps(keyboardDismissMode)}
        keyExtractor={getRowKey}
        ListFooterComponent={listFooter}
        ListHeaderComponent={listHeader}
        maintainVisibleContentPosition
        onContentSizeChange={onContentSizeChange}
        onEndReached={onEndReached}
        onEndReachedThreshold={0.5}
        onNearEndChange={onNearEndChange}
        recycleItems
        renderItem={({ index, item }) => renderRow(item, undefined, index === rows.length - 1)}
        scrollPosition={scrollPosition}
        suppressContentGap={topPadding !== undefined}
        testID="morpheme-details-tile-list"
      />
    </View>
  );
}

export function MorphemeDetailsTileList(
  props: React.ComponentProps<typeof MorphemeDetailsTileListContent>,
) {
  return (
    <MorphemeTilePresentationProvider>
      <MorphemeDetailsTileListContent {...props} />
    </MorphemeTilePresentationProvider>
  );
}
